import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Grid, Card,
  CardContent, Avatar, Chip, Button, IconButton, Tooltip, alpha, Divider,
  LinearProgress, Dialog, DialogTitle, DialogContent, useTheme,
  ImageList, ImageListItem, ImageListItemBar, Tabs, Tab, useMediaQuery
} from '@mui/material';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../context/AuthContext';
import {
  fetchMySubscriptions,
  fetchReceivedImages,
  fetchSentImages,
  uploadReceivedImages,
} from '../designApi';

const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.images)) return data.images;
  if (Array.isArray(data.subscriptions)) return data.subscriptions;
  if (Array.isArray(data.plans)) return data.plans;
  if (Array.isArray(data.wallets)) return data.wallets;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

const getField = (obj, ...fields) => {
  if (!obj) return null;
  for (const field of fields) {
    const parts = field.split('.');
    let val = obj;
    for (const part of parts) {
      val = val?.[part];
      if (val === undefined || val === null) break;
    }
    if (val !== undefined && val !== null) return val;
  }
  return null;
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const STATUS_CONFIG = {
  pending: { color: '#ed6c02', icon: <PendingIcon />, label: 'Pending Approval' },
  approved: { color: '#2e7d32', icon: <CheckCircleIcon />, label: 'Approved' },
  rejected: { color: '#d32f2f', icon: <CancelIcon />, label: 'Rejected' },
  completed: { color: '#1565c0', icon: <CheckCircleIcon />, label: 'Completed' }
};

export default function MyDesignSubscriptions() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedSub, setSelectedSub] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [receivedImages, setReceivedImages] = useState([]);
  const [sentImages, setSentImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);

  // ✅ UPDATED: Added downloadUrl to previewDialog state
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    imageUrl: '',
    downloadUrl: '',
    title: ''
  });

  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const userId = getField(user, 'Id', 'id', 'userId', 'UserId');
      if (!userId) {
        setSubscriptions([]);
        return;
      }
      const data = await fetchMySubscriptions(userId);
      const subsArray = extractArray(data);
      setSubscriptions(subsArray);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load your subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const loadImages = async (subscriptionId) => {
    try {
      setLoadingImages(true);
      setError('');
      const [receivedData, sentData] = await Promise.all([
        fetchReceivedImages(subscriptionId),
        fetchSentImages(subscriptionId)
      ]);
      const receivedArr = extractArray(receivedData);
      const sentArr = extractArray(sentData);
      setReceivedImages(receivedArr);
      setSentImages(sentArr);
    } catch (err) {
      console.error('Failed to load images:', err);
      setError('Failed to load images');
      setReceivedImages([]);
      setSentImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleOpenDetail = async (sub) => {
    setSelectedSub(sub);
    setDetailOpen(true);
    setActiveTab(0);
    const subId = getField(sub, 'Id', 'id');
    if (subId) {
      await loadImages(subId);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedSub(null);
    setReceivedImages([]);
    setSentImages([]);
  };

  const handleUploadReceivedImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !selectedSub) return;

    const numDesigns = getField(selectedSub, 'NumberOfDesigns', 'numberOfDesigns') || 0;
    const remaining = getField(selectedSub, 'RemainingUploads', 'remainingUploads')
      ?? (numDesigns - receivedImages.length);

    if (files.length > remaining) {
      setError(`You can only upload ${remaining} more image(s). Currently ${receivedImages.length}/${numDesigns} uploaded.`);
      return;
    }

    const invalidFiles = files.filter(f => !f.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      setError('Please upload only image files (JPG, PNG, WEBP)');
      return;
    }

    const oversizedFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Each file must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const base64Images = await Promise.all(
        files.map(file => fileToBase64(file))
      );

      const invalidImages = base64Images.filter(img => !img || !img.startsWith('data:image'));
      if (invalidImages.length > 0) {
        setError('Failed to process some images. Please try again.');
        setUploading(false);
        return;
      }

      const subId = getField(selectedSub, 'Id', 'id');
      const userId = getField(user, 'Id', 'id', 'userId', 'UserId');

      const requestBody = {
        adminId: parseInt(userId),
        images: base64Images
      };

      const result = await uploadReceivedImages(subId, requestBody);

      setSuccess(result?.message || `${files.length} image(s) uploaded successfully`);

      if (result && selectedSub) {
        const updatedSub = { ...selectedSub };
        if (result.uploadedCount !== undefined) {
          updatedSub.UploadedDesignsCount = result.uploadedCount;
        }
        if (result.remaining !== undefined) {
          updatedSub.RemainingUploads = result.remaining;
        }
        setSelectedSub(updatedSub);
      }

      await loadImages(subId);
      await loadSubscriptions();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ✅ FIXED: Uses backend DownloadUrl (Content-Disposition: attachment) with fallback to imageUrl blob
  const handleDownloadImage = async (downloadUrl, imageUrl, fallbackFilename) => {
    // Priority: downloadUrl from backend API → imageUrl as blob fallback
    const primaryUrl = downloadUrl || imageUrl;
    if (!primaryUrl) return;

    try {
      // Step 1: Try the primary URL (backend download endpoint with Content-Disposition: attachment)
      const response = await fetch(primaryUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const contentType = response.headers.get('Content-Type') || '';

      // If the response is HTML (error page), skip to fallback
      if (contentType.includes('text/html')) {
        throw new Error('Received HTML instead of image — endpoint not reachable');
      }

      const blob = await response.blob();

      // Check if blob is actually an image (not empty or error)
      if (blob.size < 100 && contentType.includes('text')) {
        throw new Error('Response is not a valid image');
      }

      // Try to get filename from Content-Disposition header (backend sets this)
      let serverFilename = null;
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          serverFilename = match[1].replace(/['"]/g, '');
        }
      }

      // Get extension from content-type
      let extension = 'jpg';
      if (contentType.includes('png')) extension = 'png';
      else if (contentType.includes('jpeg') || contentType.includes('jpg')) extension = 'jpg';
      else if (contentType.includes('webp')) extension = 'webp';
      else if (contentType.includes('gif')) extension = 'gif';
      else if (contentType.includes('svg')) extension = 'svg';
      else if (contentType.includes('bmp')) extension = 'bmp';

      // Also try extracting extension from URL path
      try {
        const urlPath = new URL(primaryUrl).pathname;
        const urlExt = urlPath.split('.').pop()?.toLowerCase();
        if (urlExt && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'bmp'].includes(urlExt)) {
          extension = urlExt;
        }
      } catch (e) {
        // ignore
      }

      // Use server filename if available, otherwise build one
      const filename = serverFilename || `${fallbackFilename || 'design-image'}.${extension}`;

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 300);

    } catch (err) {
      console.error('Primary download failed, trying imageUrl fallback:', err);

      // Step 2: Fallback — fetch imageUrl directly as blob
      if (imageUrl && imageUrl !== primaryUrl) {
        try {
          const resp = await fetch(imageUrl);
          if (!resp.ok) throw new Error(`Fallback fetch failed: ${resp.status}`);

          const blob = await resp.blob();
          const ct = resp.headers.get('Content-Type') || '';
          let ext = 'jpg';
          if (ct.includes('png')) ext = 'png';
          else if (ct.includes('webp')) ext = 'webp';
          else if (ct.includes('gif')) ext = 'gif';
          else if (ct.includes('svg')) ext = 'svg';

          try {
            const urlPath = new URL(imageUrl).pathname;
            const urlExt = urlPath.split('.').pop()?.toLowerCase();
            if (urlExt && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'bmp'].includes(urlExt)) {
              ext = urlExt;
            }
          } catch (e) { /* ignore */ }

          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `${fallbackFilename || 'design-image'}.${ext}`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 300);
          return;
        } catch (fallbackErr) {
          console.error('Blob fallback also failed:', fallbackErr);
        }
      }

      // Step 3: Last resort — open in new tab (at least user can right-click save)
      try {
        const link = document.createElement('a');
        link.href = imageUrl || primaryUrl;
        link.download = `${fallbackFilename || 'design-image'}.jpg`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (lastErr) {
        console.error('All download attempts failed:', lastErr);
      }
    }
  };

  const getProgress = (sub) => {
    if (!sub) return { received: 0, sent: 0, total: 1, receivedPercent: 0, sentPercent: 0, isComplete: false };
    const total = getField(sub, 'NumberOfDesigns', 'numberOfDesigns') || 1;
    const received = getField(sub, 'UploadedDesignsCount', 'uploadedDesignsCount') || 0;
    const sent = getField(sub, 'SentDesignsCount', 'sentDesignsCount') || 0;
    const status = getField(sub, 'Status', 'status') || '';
    return {
      received,
      sent,
      total,
      receivedPercent: Math.min((received / total) * 100, 100),
      sentPercent: Math.min((sent / total) * 100, 100),
      isComplete: status === 'completed' || sent >= total
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  const safeSubs = Array.isArray(subscriptions) ? subscriptions : [];
  const activeSubs = safeSubs.filter(s => {
    const status = getField(s, 'Status', 'status');
    return status === 'approved' || status === 'completed';
  });
  const pendingSubs = safeSubs.filter(s => getField(s, 'Status', 'status') === 'pending');
  const rejectedSubs = safeSubs.filter(s => getField(s, 'Status', 'status') === 'rejected');

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#6a1b9a', 0.08)} 0%, ${alpha('#ad1457', 0.08)} 100%)`,
          border: `1px solid ${alpha('#6a1b9a', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#6a1b9a', width: 50, height: 50 }}>
              <DesignServicesIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#6a1b9a">
                {t('MySubscriptions') || 'My Design Subscriptions'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('MySubsDesc') || 'Manage your design plan subscriptions and images'}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title={t('Refresh') || 'Refresh'}>
            <IconButton onClick={loadSubscriptions} sx={{ bgcolor: alpha('#6a1b9a', 0.1) }}>
              <RefreshIcon sx={{ color: '#6a1b9a' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#2e7d32', 0.15)}`, p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32' }}>
                <CheckCircleIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{activeSubs.length}</Typography>
                <Typography variant="caption" color="text.secondary">{t('ActiveSubscriptions') || 'Active'}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#ed6c02', 0.15)}`, p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#ed6c02', 0.1), color: '#ed6c02' }}>
                <PendingIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{pendingSubs.length}</Typography>
                <Typography variant="caption" color="text.secondary">{t('PendingApproval') || 'Pending Approval'}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#d32f2f', 0.15)}`, p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#d32f2f', 0.1), color: '#d32f2f' }}>
                <CancelIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{rejectedSubs.length}</Typography>
                <Typography variant="caption" color="text.secondary">{t('Rejected') || 'Rejected'}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Subscriptions List */}
      {safeSubs.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha('#000', 0.2)}` }}>
          <DesignServicesIcon sx={{ fontSize: 64, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('NoSubscriptions') || 'No subscriptions yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('PurchasePlanPrompt') || 'Go to Purchase Plan to get started'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {safeSubs.map((sub, index) => {
            const subId = getField(sub, 'Id', 'id');
            const subSubscriptionId = getField(sub, 'SubscriptionId', 'subscriptionId') || subId || '-';
            const subStatus = getField(sub, 'Status', 'status') || 'pending';
            const subPlanName = getField(sub, 'PlanName', 'planName', 'Plan.Name') || 'Plan';
            const subDesigns = getField(sub, 'NumberOfDesigns', 'numberOfDesigns') || 0;
            const subFormattedAmount = getField(sub, 'FormattedAmount', 'formattedAmount');
            const subAmount = getField(sub, 'Amount', 'amount') || 0;
            const subCurrency = getField(sub, 'Currency', 'currency') || 'YER';
            const subCurrencySymbol = getField(sub, 'CurrencySymbol', 'currencySymbol') || '';
            const subCreatedAt = getField(sub, 'CreatedAt', 'createdAt');
            const subRejectionReason = getField(sub, 'RejectionReason', 'rejectionReason');

            const progress = getProgress(sub);
            const statusConf = STATUS_CONFIG[subStatus] || STATUS_CONFIG.pending;

            const displayAmount = subFormattedAmount || `${subCurrencySymbol}${subAmount}` || `${subAmount} ${subCurrency}`;

            return (
              <Grid item xs={12} sm={6} md={4} key={subId || index}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(statusConf.color, 0.2)}`,
                    transition: 'all 0.3s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: `0 4px 20px ${alpha(statusConf.color, 0.15)}`,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Chip
                        icon={React.cloneElement(statusConf.icon, { sx: { fontSize: 16 } })}
                        label={t(statusConf.label) || statusConf.label}
                        size="small"
                        sx={{
                          bgcolor: alpha(statusConf.color, 0.1),
                          color: statusConf.color,
                          fontWeight: 700,
                          fontSize: '0.7rem'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(subCreatedAt)}
                      </Typography>
                    </Stack>

                    <Chip
                      label={subSubscriptionId}
                      size="small"
                      sx={{ bgcolor: alpha('#000', 0.04), fontWeight: 600, fontSize: '0.7rem', mb: 2 }}
                    />

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#ad1457', 0.1), width: 40, height: 40 }}>
                        <DesignServicesIcon sx={{ color: '#ad1457', fontSize: 22 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight={700}>{subPlanName}</Typography>
                        <Chip
                          icon={<ImageIcon sx={{ fontSize: 14 }} />}
                          label={`${subDesigns} designs`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', mt: 0.3 }}
                        />
                      </Box>
                    </Stack>

                    <Paper sx={{ p: 1.5, bgcolor: alpha('#000', 0.02), borderRadius: 2, mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">{t('Amount') || 'Amount'}</Typography>
                        <Typography variant="body1" fontWeight={800} color="#ad1457">
                          {displayAmount}
                        </Typography>
                      </Stack>
                    </Paper>

                    {(subStatus === 'approved' || subStatus === 'completed') && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mb: 1.5 }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={600} color="#1565c0">
                              {t('Uploaded') || 'Uploaded'}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="#1565c0">
                              {progress.received} / {progress.total}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={progress.receivedPercent}
                            sx={{
                              height: 6, borderRadius: 3,
                              bgcolor: alpha('#1565c0', 0.1),
                              '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#1565c0' }
                            }}
                          />
                        </Box>
                        <Box>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={600} color="#2e7d32">
                              {t('DesignsReceived') || 'Designs Received'}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="#2e7d32">
                              {progress.sent} / {progress.total}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={progress.sentPercent}
                            sx={{
                              height: 6, borderRadius: 3,
                              bgcolor: alpha('#2e7d32', 0.1),
                              '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#2e7d32' }
                            }}
                          />
                        </Box>
                      </>
                    )}

                    {subStatus === 'rejected' && subRejectionReason && (
                      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        <Typography variant="caption" fontWeight={600}>
                          {t('RejectionReason') || 'Reason'}: {subRejectionReason}
                        </Typography>
                      </Alert>
                    )}

                    {subStatus === 'pending' && (
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} icon={<HourglassTopIcon fontSize="small" />}>
                        <Typography variant="caption">
                          {t('PendingApprovalMsg') || 'Your payment is being verified'}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>

                  {(subStatus === 'approved' || subStatus === 'completed') && (
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleOpenDetail(sub)}
                        sx={{
                          textTransform: 'none', borderRadius: 2, fontWeight: 600,
                          bgcolor: statusConf.color,
                          '&:hover': { bgcolor: alpha(statusConf.color, 0.85) }
                        }}
                      >
                        {progress.isComplete
                          ? (t('ViewDesigns') || 'View Designs')
                          : (t('ManageImages') || 'Manage Images')
                        }
                      </Button>
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Subscription Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha('#6a1b9a', 0.05) }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#6a1b9a', width: 40, height: 40 }}>
              <DesignServicesIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {getField(selectedSub, 'PlanName', 'planName') || 'Plan'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getField(selectedSub, 'SubscriptionId', 'subscriptionId') || ''}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleCloseDetail}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedSub && (() => {
            const spDesigns = getField(selectedSub, 'NumberOfDesigns', 'numberOfDesigns') || 0;
            const subStatus = getField(selectedSub, 'Status', 'status') || '';
            const safeReceived = Array.isArray(receivedImages) ? receivedImages : [];
            const safeSent = Array.isArray(sentImages) ? sentImages : [];
            const isComplete = subStatus === 'completed';
            const remainingUploads = getField(selectedSub, 'RemainingUploads', 'remainingUploads')
              ?? (spDesigns - safeReceived.length);
            const canUpload = !isComplete && remainingUploads > 0 && subStatus === 'approved';

            return (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  sx={{
                    borderBottom: `1px solid ${alpha('#000', 0.08)}`,
                    px: 2,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }
                  }}
                >
                  <Tab
                    icon={<CloudUploadIcon sx={{ fontSize: 20 }} />}
                    iconPosition="start"
                    label={`${t('UploadImages') || 'Upload Images'} (${safeReceived.length}/${spDesigns})`}
                  />
                  <Tab
                    icon={<DownloadIcon sx={{ fontSize: 20 }} />}
                    iconPosition="start"
                    label={`${t('ReceivedDesigns') || 'Received Designs'} (${safeSent.length}/${spDesigns})`}
                  />
                </Tabs>

                <Box sx={{ p: 3 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
                      {success}
                    </Alert>
                  )}

                  {loadingImages ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Tab 0: Upload Images (received by backend) */}
                      {activeTab === 0 && (
                        <Box>
                          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            <Typography variant="body2">
                              {t('UploadImagesDesc') || 'Upload your images so our designers can work on them. You have paid for'}{' '}
                              <strong>{spDesigns} {t('designs') || 'designs'}</strong>.
                              {' '}{t('Uploaded') || 'Uploaded'}: <strong>{safeReceived.length}</strong>,{' '}
                              {t('Remaining') || 'Remaining'}: <strong>{remainingUploads}</strong>
                            </Typography>
                          </Alert>

                          {canUpload && (
                            <Button
                              fullWidth
                              variant="outlined"
                              startIcon={uploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                              sx={{
                                mb: 3, py: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                borderColor: alpha('#1565c0', 0.3), color: '#1565c0', borderStyle: 'dashed',
                                '&:hover': { borderColor: '#1565c0', bgcolor: alpha('#1565c0', 0.04) }
                              }}
                            >
                              {uploading
                                ? (t('Uploading') || 'Uploading...')
                                : `${t('UploadImages') || 'Upload Images'} (${remainingUploads} ${t('Remaining') || 'remaining'})`
                              }
                            </Button>
                          )}

                          {isComplete && (
                            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {t('SubscriptionCompleted') || 'This subscription is completed. No more uploads allowed.'}
                              </Typography>
                            </Alert>
                          )}

                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleUploadReceivedImages}
                            style={{ display: 'none' }}
                          />

                          {uploading && (
                            <LinearProgress
                              variant="indeterminate"
                              sx={{
                                mb: 2, height: 6, borderRadius: 3,
                                bgcolor: alpha('#1565c0', 0.1),
                                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#1565c0' }
                              }}
                            />
                          )}

                          {safeReceived.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6, bgcolor: alpha('#000', 0.02), borderRadius: 2 }}>
                              <ImageIcon sx={{ fontSize: 64, color: alpha('#000', 0.12), mb: 1 }} />
                              <Typography variant="body1" color="text.secondary" fontWeight={600}>
                                {t('NoImagesUploaded') || 'No images uploaded yet'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('UploadImagesToStart') || 'Upload your images to start the design process'}
                              </Typography>
                            </Box>
                          ) : (
                            <ImageList cols={isMobile ? 2 : 4} gap={12}>
                              {safeReceived.map((img, index) => {
                                const imgId = getField(img, 'Id', 'id');
                                const imgUrl = getField(img, 'ImageUrl', 'imageUrl') || '';
                                const imgDownloadUrl = getField(img, 'DownloadUrl', 'downloadUrl') || '';
                                const imgCreatedAt = getField(img, 'CreatedAt', 'createdAt');

                                return (
                                  <ImageListItem
                                    key={imgId || index}
                                    sx={{
                                      borderRadius: 2, overflow: 'hidden',
                                      border: `1px solid ${alpha('#1565c0', 0.15)}`,
                                      cursor: 'pointer', transition: 'all 0.2s',
                                      '&:hover': { boxShadow: `0 4px 12px ${alpha('#000', 0.1)}` }
                                    }}
                                  >
                                    <img
                                      src={imgUrl}
                                      alt={`Upload ${index + 1}`}
                                      loading="lazy"
                                      style={{ height: 180, objectFit: 'cover' }}
                                      onClick={() => setPreviewDialog({
                                        open: true,
                                        imageUrl: imgUrl,
                                        downloadUrl: imgDownloadUrl,
                                        title: `${t('UploadedImage') || 'Uploaded Image'} #${index + 1}`
                                      })}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <ImageListItemBar
                                      title={`#${index + 1}`}
                                      subtitle={imgCreatedAt ? formatDate(imgCreatedAt) : ''}
                                      actionIcon={
                                        <Stack direction="row" spacing={0.5} sx={{ mr: 0.5 }}>
                                          <Tooltip title={t('Preview') || 'Preview'}>
                                            <IconButton
                                              size="small"
                                              sx={{ color: '#fff' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewDialog({
                                                  open: true,
                                                  imageUrl: imgUrl,
                                                  downloadUrl: imgDownloadUrl,
                                                  title: `${t('UploadedImage') || 'Uploaded Image'} #${index + 1}`
                                                });
                                              }}
                                            >
                                              <ZoomInIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          {/* ✅ FIXED: Uses DownloadUrl from backend API */}
                                          <Tooltip title={t('Download') || 'Download'}>
                                            <IconButton
                                              size="small"
                                              sx={{ color: '#fff' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownloadImage(imgDownloadUrl, imgUrl, `uploaded-${index + 1}`);
                                              }}
                                            >
                                              <SaveAltIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      }
                                      sx={{
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)'
                                      }}
                                    />
                                  </ImageListItem>
                                );
                              })}
                            </ImageList>
                          )}
                        </Box>
                      )}

                      {/* Tab 1: Received Designs (sent images from designer) */}
                      {activeTab === 1 && (
                        <Box>
                          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            <Typography variant="body2">
                              {t('ReceivedDesignsDesc') || 'These are the completed designs sent back to you by the design team.'}
                              {' '}{t('DesignsReceived') || 'Designs received'}: <strong>{safeSent.length}/{spDesigns}</strong>
                            </Typography>
                          </Alert>

                          {safeSent.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6, bgcolor: alpha('#000', 0.02), borderRadius: 2 }}>
                              <DesignServicesIcon sx={{ fontSize: 64, color: alpha('#000', 0.12), mb: 1 }} />
                              <Typography variant="body1" color="text.secondary" fontWeight={600}>
                                {t('NoDesignsReceived') || 'No designs received yet'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('DesignsWillAppear') || 'Completed designs will appear here once ready'}
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                                <Chip
                                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                  label={`${safeSent.length} / ${spDesigns} ${t('DesignsReady') || 'designs ready'}`}
                                  sx={{
                                    bgcolor: alpha('#2e7d32', 0.1),
                                    color: '#2e7d32',
                                    fontWeight: 600
                                  }}
                                />
                              </Stack>

                              <ImageList cols={isMobile ? 2 : 4} gap={12}>
                                {safeSent.map((img, index) => {
                                  const imgId = getField(img, 'Id', 'id');
                                  const imgUrl = getField(img, 'ImageUrl', 'imageUrl') || '';
                                  const imgDownloadUrl = getField(img, 'DownloadUrl', 'downloadUrl') || '';
                                  const imgCreatedAt = getField(img, 'CreatedAt', 'createdAt');

                                  return (
                                    <ImageListItem
                                      key={imgId || index}
                                      sx={{
                                        borderRadius: 2, overflow: 'hidden',
                                        border: `1px solid ${alpha('#2e7d32', 0.15)}`,
                                        cursor: 'pointer', transition: 'all 0.2s',
                                        '&:hover': { boxShadow: `0 4px 12px ${alpha('#000', 0.1)}` }
                                      }}
                                    >
                                      <img
                                        src={imgUrl}
                                        alt={`Design ${index + 1}`}
                                        loading="lazy"
                                        style={{ height: 180, objectFit: 'cover' }}
                                        onClick={() => setPreviewDialog({
                                          open: true,
                                          imageUrl: imgUrl,
                                          downloadUrl: imgDownloadUrl,
                                          title: `${t('ReceivedDesign') || 'Received Design'} #${index + 1}`
                                        })}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                      <ImageListItemBar
                                        title={`${t('Design') || 'Design'} #${index + 1}`}
                                        subtitle={imgCreatedAt ? formatDate(imgCreatedAt) : ''}
                                        actionIcon={
                                          <Stack direction="row" spacing={0.5} sx={{ mr: 0.5 }}>
                                            <Tooltip title={t('Preview') || 'Preview'}>
                                              <IconButton
                                                size="small"
                                                sx={{ color: '#fff' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setPreviewDialog({
                                                    open: true,
                                                    imageUrl: imgUrl,
                                                    downloadUrl: imgDownloadUrl,
                                                    title: `${t('ReceivedDesign') || 'Received Design'} #${index + 1}`
                                                  });
                                                }}
                                              >
                                                <ZoomInIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            {/* ✅ FIXED: Uses DownloadUrl from backend API — NO delete button */}
                                            <Tooltip title={t('Download') || 'Download'}>
                                              <IconButton
                                                size="small"
                                                sx={{ color: '#fff' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownloadImage(imgDownloadUrl, imgUrl, `design-${index + 1}`);
                                                }}
                                              >
                                                <SaveAltIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Stack>
                                        }
                                        sx={{
                                          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)'
                                        }}
                                      />
                                    </ImageListItem>
                                  );
                                })}
                              </ImageList>
                            </>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, imageUrl: '', downloadUrl: '', title: '' })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewDialog.title}
          <Stack direction="row" spacing={1}>
            {/* ✅ FIXED: Uses downloadUrl from previewDialog state */}
            <Tooltip title={t('Download') || 'Download'}>
              <IconButton
                onClick={() => handleDownloadImage(
                  previewDialog.downloadUrl,
                  previewDialog.imageUrl,
                  previewDialog.title
                )}
                sx={{ color: '#1565c0' }}
              >
                <SaveAltIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setPreviewDialog({ open: false, imageUrl: '', downloadUrl: '', title: '' })}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {previewDialog.imageUrl && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <img
                src={previewDialog.imageUrl}
                alt={previewDialog.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  borderRadius: 8
                }}
                onError={(e) => {
                  e.target.alt = 'Failed to load image';
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}